// Record a drug given to the patient on an incident.
//
// Three narrowing choices — the drug, which stocked one of it (told apart by where
// it is), and which batch — then the amount. Saving records the administration on
// the incident and takes that amount off the batch in the stock ledger, both in one
// request (POST /incidents/{id}/drugs; core/incident_drugs.py).
//
// Part of one unit — a tube of cream — is its own choice: it opens a unit there if
// none is open (still counted as stock), and "this uses it up" takes it out.
//
// The clinical record is the route and the dose, both shaped by the drug type: one
// route recorded on it is simply shown, several are a choice to make (IM or IV), none
// offers the whole list. The dose is always in the type's unit; with a single dose
// recorded (500 mg a tablet) it's "how many of them", and the server multiplies it
// out (core/incident_drugs.dose) — without one, the amount is typed in. What comes
// off stock is asked separately, since it isn't the dose (one inhaler, four puffs),
// but follows a whole number of doses until it's edited.
//
// The options come from the incident, not from /inventory: whoever gives a drug at
// an event holds no inventory permissions, so the endpoint behind this dialog is
// gated by Incidents.giveDrug and serves only the drug rows.
//
// `deductsStock` is false before the event's start date, when the record is made
// but the stock is left alone so setting-up-and-trying-it-out incidents can't empty
// the bags. The server decides that from the event (deducts_stock in
// core/incident_drugs.py); this only says so on screen.
import { useEffect, useState } from "react";
import axios from "axios";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText,
  Button, Stack, TextField, MenuItem, Typography, Alert, CircularProgress, Box,
  FormControlLabel, Checkbox, InputAdornment,
} from "@mui/material";
import { API_BASE_URL } from "../config";
import { notify } from "../notify";
import { DRUG_DOSE_UNITS, DRUG_ROUTES } from "../constants";
import { doseAmount } from "../incidentNotes";
import HintedSelect from "./HintedSelect";

const INCIDENTS_API = `${API_BASE_URL}/incidents`;

// Local date-only formatter (avoids the UTC off-by-one on a "YYYY-MM-DD" value).
const fmtDate = (iso) => {
  if (!iso) return "no expiry date";
  const [y, m, d] = String(iso).slice(0, 10).split("-").map(Number);
  if (!y) return "no expiry date";
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    day: "numeric", month: "short", year: "numeric",
  });
};

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

// Expired for flagging purposes, which is the printed date plus the drug type's
// expiry grace in whole years (inventory_types.expiry_grace_years, served with the
// options). No grace — the usual case — leaves the printed date as the deadline. Same
// rule as expiryFlag() on the inventory page, so a batch isn't called out here and
// left alone there.
const isExpired = (iso, graceYears) => {
  if (!iso) return false;
  const grace = Number(graceYears) || 0;
  if (!grace) return iso < todayISO();
  const [y, m, d] = String(iso).slice(0, 10).split("-").map(Number);
  if (!y) return false;
  const at = new Date(y + grace, m - 1, d);
  return `${at.getFullYear()}-${String(at.getMonth() + 1).padStart(2, "0")}-${String(at.getDate()).padStart(2, "0")}`
    < todayISO();
};

// A batch is chosen by its batch number and expiry date — the two things printed on
// the pack — with what the app thinks is left, since that's what the count will
// drop from, and how many of those are already opened.
const batchLabel = (b, graceYears) => {
  const expired = isExpired(b.expiry_date, graceYears) ? " — EXPIRED" : "";
  const recalled = b.recalled ? " — RECALLED" : "";
  const opened = b.opened ? `, ${b.opened} opened` : "";
  return `${b.serial_number || "No batch number"} — expires ${fmtDate(b.expiry_date)}${expired}${recalled}`
    + ` (${b.quantity ?? 0} left${opened})`;
};

const itemLabel = (i) => (i.location ? `${i.location} / ${i.label}` : i.label);

export default function GiveDrugDialog({ open, onClose, onSaved, incidentId,
                                        deductsStock = true }) {
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState([]);   // [{ type_id, name, items: [...] }]
  const [typeId, setTypeId] = useState("");
  const [itemId, setItemId] = useState("");
  const [batchId, setBatchId] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [quantityTouched, setQuantityTouched] = useState(false);
  const [route, setRoute] = useState("");
  const [doses, setDoses] = useState(1);                // × the type's single dose
  const [doseQuantity, setDoseQuantity] = useState(""); // when it has none
  const [doseUnit, setDoseUnit] = useState("");         // when the type has no unit
  const [notes, setNotes] = useState("");
  const [partUnit, setPartUnit] = useState(false);
  const [usesUp, setUsesUp] = useState(false);
  const [saving, setSaving] = useState(false);

  // Fetch fresh each time it opens: someone else on the event may have given the
  // last of a batch since the incident was opened.
  useEffect(() => {
    if (!open || !incidentId) return;
    setTypeId(""); setItemId(""); setBatchId(""); setNotes("");
    setRoute(""); setDoses(1); setDoseQuantity(""); setDoseUnit("");
    setQuantity(1); setQuantityTouched(false);
    setPartUnit(false); setUsesUp(false);
    setLoading(true);
    axios.get(`${INCIDENTS_API}/${incidentId}/drug-options`)
      .then(res => setOptions(res.data || []))
      .catch(() => setOptions([]))
      .finally(() => setLoading(false));
  }, [open, incidentId]);

  const drug = options.find(o => o.type_id === typeId) || null;
  const item = (drug?.items || []).find(i => i.id === itemId) || null;
  const batches = item?.batches || [];
  const batch = batches.find(b => b.id === batchId) || null;

  // Picking the drug picks where it comes from too when it's only held in one
  // place; the same for the batch, so the field is only asked about when there is
  // actually a choice to make.
  // The dose fields start again with each drug: its routes and unit are its own.
  function resetDose(next) {
    setRoute(next?.routes?.length === 1 ? next.routes[0] : "");
    setDoses(1); setDoseQuantity(""); setDoseUnit("");
    setQuantity(1); setQuantityTouched(false);
  }

  const onDrug = (id) => {
    const next = options.find(o => o.type_id === id) || null;
    const only = next?.items?.length === 1 ? next.items[0] : null;
    setTypeId(id);
    resetDose(next);
    setItemId(only ? only.id : "");
    setBatchId(only && only.batches.length === 1 ? only.batches[0].id : "");
  };

  const onItem = (id) => {
    const next = (drug?.items || []).find(i => i.id === id) || null;
    setItemId(id);
    setBatchId(next && next.batches.length === 1 ? next.batches[0].id : "");
  };

  // Two of a 500 mg tablet is two tablets out of the bag — until somebody says
  // otherwise, which is for a dose that isn't whole units of stock (puffs, drops).
  const onDoses = (value) => {
    setDoses(value);
    const n = Number(value);
    if (!quantityTouched && Number.isInteger(n) && n >= 1) setQuantity(n);
  };

  const routes = drug?.routes || [];
  // Hints come from the full list, so "IM" still says what it stands for.
  const routeOptions = (routes.length ? routes : DRUG_ROUTES.map(r => r.value))
    .map(v => DRUG_ROUTES.find(r => r.value === v) || { value: v });
  const unit = drug?.dose_unit || doseUnit;
  const single = Number(drug?.dose_quantity) || 0;
  const dosesN = Number(doses);
  const total = single ? dosesN * single : Number(doseQuantity);
  const doseOk = single ? dosesN > 0 : Number(doseQuantity) > 0;
  const routeOk = !!route;
  const unitOk = !!unit;

  // What the app thinks is there. Only ever a warning — see core/stock.py: a stale
  // count mustn't stop the record being made.
  const available = batch ? (batch.quantity ?? 0) : (item ? (item.quantity ?? 0) : 0);
  const qty = partUnit ? 1 : Number(quantity);
  const qtyOk = Number.isInteger(qty) && qty >= 1;
  // Nothing is coming off the count before the event starts, or when only opening
  // a unit, so there is nothing to be short of.
  const takesStock = deductsStock && (!partUnit || usesUp);
  const short = qtyOk && item && qty > available && takesStock;
  const needsBatch = batches.length > 1 && !batchId;
  // Every drug is held in batches, so a line with none recorded has nothing to
  // record the administration against.
  const noStock = !!item && batches.length === 0;
  const canSave = !!item && !needsBatch && !noStock && qtyOk && routeOk && doseOk && unitOk
    && !saving;

  const save = () => {
    setSaving(true);
    axios.post(`${INCIDENTS_API}/${incidentId}/drugs`, {
      item_id: itemId,
      batch_id: batchId || null,
      quantity: qty,
      part_unit: partUnit,
      finishes_unit: partUnit && usesUp,
      route,
      ...(single ? { doses: dosesN } : { dose_quantity: Number(doseQuantity) }),
      dose_unit: drug?.dose_unit ? null : doseUnit,
      notes: notes.trim() || null,
    }).then(() => {
      notify(!deductsStock
        ? "Drug recorded on the incident. Stock unchanged — the event hasn't started."
        : partUnit && !usesUp
          ? "Drug recorded on the incident; the unit is marked as opened."
          : "Drug recorded on the incident and taken out of stock.",
        "success");
      onSaved && onSaved();
      onClose();
    }).catch(() => {}) // the global interceptor surfaces the error
      .finally(() => setSaving(false));
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Give a drug</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <DialogContentText>
            Records what was given on this incident
            {deductsStock ? ", and takes it out of the stock it came from." : "."}
          </DialogContentText>

          {!deductsStock && (
            <Alert severity="info">
              This event hasn't started yet, so nothing will come off the inventory —
              the drug given is still recorded. Stock is deducted from the event's
              start date.
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : options.length === 0 ? (
            <Alert severity="info">
              No drugs are stocked in the inventory yet, so there is nothing to record.
            </Alert>
          ) : (
            <>
              <TextField label="Drug" select fullWidth value={typeId}
                         onChange={e => onDrug(e.target.value)}>
                {options.map(o => (
                  <MenuItem key={o.type_id} value={o.type_id}>{o.name}</MenuItem>
                ))}
              </TextField>

              <TextField label="Taken from" select fullWidth value={itemId}
                         disabled={!drug} onChange={e => onItem(e.target.value)}
                         helperText={drug ? "Where the drug came from" : "Choose a drug first"}>
                {(drug?.items || []).map(i => (
                  <MenuItem key={i.id} value={i.id}>
                    <Box>
                      <Typography variant="body2">{itemLabel(i)}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                        {i.quantity ?? 0} in stock
                        {i.batches.length > 1 ? ` · ${i.batches.length} batches` : ""}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </TextField>

              {/* Only asked when there is more than one batch; a single batch is
                  already chosen, and shown below so it's still on the record. */}
              {batches.length > 1 && (
                <TextField label="Batch" select fullWidth value={batchId}
                           onChange={e => setBatchId(e.target.value)}
                           helperText="Which batch it came out of">
                  {batches.map(b => (
                    <MenuItem key={b.id} value={b.id}>
                      {batchLabel(b, drug?.expiry_grace_years)}
                    </MenuItem>
                  ))}
                </TextField>
              )}
              {batches.length === 1 && batch && (
                <Typography variant="caption" color="text.secondary">
                  Batch: {batchLabel(batch, drug?.expiry_grace_years)}
                </Typography>
              )}
              {batch?.recalled && (
                <Alert severity="error">
                  This batch has been recalled by the supplier. Use another batch if
                  you can, and tell the Clinical Manager if it has been given.
                </Alert>
              )}
              {noStock && (
                <Alert severity="warning">
                  No stock of this drug is recorded there, so there's no batch to record
                  it against. Choose where it came from, or ask for the count to be
                  corrected with a kit check.
                </Alert>
              )}

              {drug && (
                <>
                  {/* One route on the drug type is simply what it is. */}
                  {routes.length === 1 ? (
                    <TextField label="Route" fullWidth disabled value={routes[0]}
                               helperText={DRUG_ROUTES.find(r => r.value === routes[0])?.hint
                                 || "The only route this drug is given by"} />
                  ) : (
                    <HintedSelect label="Route" required allowBlank={false}
                                  options={routeOptions} value={route} onChange={setRoute}
                                  helperText={routes.length
                                    ? `This drug can be given ${routes.join(" or ")} — say which`
                                    : "No route is set on this drug type — say which"} />
                  )}

                  <Stack direction="row" spacing={2} sx={{ alignItems: "flex-start" }}>
                    {single ? (
                      <TextField label="Doses given" type="number" required value={doses}
                                 onChange={e => onDoses(e.target.value)} error={!doseOk}
                                 sx={{ flex: 1 }}
                                 slotProps={{
                                   htmlInput: { min: 0, step: "any" },
                                   input: { endAdornment: (
                                     <InputAdornment position="end">
                                       × {doseAmount(single)} {unit}
                                     </InputAdornment>) },
                                 }}
                                 helperText={doseOk ? `Total ${doseAmount(total)} ${unit}`
                                   : "How many doses?"} />
                    ) : (
                      <TextField label="Dose given" type="number" required value={doseQuantity}
                                 onChange={e => setDoseQuantity(e.target.value)} sx={{ flex: 1 }}
                                 slotProps={{
                                   htmlInput: { min: 0, step: "any" },
                                   input: drug.dose_unit ? { endAdornment: (
                                     <InputAdornment position="end">{drug.dose_unit}</InputAdornment>) }
                                     : undefined,
                                 }}
                                 helperText="The total amount given" />
                    )}
                    {/* The unit is the drug type's; asked only when it hasn't one. */}
                    {!drug.dose_unit && (
                      <TextField label="Unit" select required value={doseUnit} sx={{ flex: 1 }}
                                 onChange={e => setDoseUnit(e.target.value)}
                                 helperText="Not set on the drug type — ask for it to be added">
                        {DRUG_DOSE_UNITS.map(u => <MenuItem key={u} value={u}>{u}</MenuItem>)}
                      </TextField>
                    )}
                  </Stack>
                </>
              )}

              <Box>
                <FormControlLabel
                  control={<Checkbox checked={partUnit}
                                     onChange={e => { setPartUnit(e.target.checked); setUsesUp(false); }} />}
                  label="Part of one unit (e.g. some of a tube of cream)" />
                {partUnit && (
                  <FormControlLabel sx={{ display: "flex", ml: 3 }}
                    control={<Checkbox checked={usesUp} onChange={e => setUsesUp(e.target.checked)} />}
                    label={batch?.opened
                      ? "This finishes the opened unit — take it out of stock"
                      : "This uses the whole unit up — take it out of stock"} />
                )}
              </Box>
              {!partUnit && (
                <TextField label="Taken from stock" type="number" value={quantity}
                           onChange={e => { setQuantity(e.target.value); setQuantityTouched(true); }}
                           slotProps={{ htmlInput: { min: 1, step: 1 } }}
                           error={!qtyOk} sx={{ maxWidth: 200 }}
                           helperText={item ? `${available} recorded as available` : " "} />
              )}

              {short && (
                <Alert severity="warning">
                  Only {available} recorded here. The drug given is still recorded — the
                  count goes to zero, so check the stock afterwards.
                </Alert>
              )}

              <TextField label="Notes" fullWidth multiline minRows={2}
                         value={notes} onChange={e => setNotes(e.target.value)}
                         helperText="Optional — anything else worth recording." />
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!canSave} onClick={save}>
          Record drug given
        </Button>
      </DialogActions>
    </Dialog>
  );
}
